/**
 * @function getHostPermissions
 * @description Calcula y retorna todos los permisos del módulo Host (SSO y gestión de catálogos).
 * Esta función fábrica es invocada por `useRolePermissions` y recibe el estado de rol del usuario
 * junto con los helpers de verificación contra las listas del endpoint, garantizando que los
 * permisos reflejen tanto el rol jerárquico como los permisos dinámicos del backend.
 *
 * Incluye permisos para:
 * - **SSO — Corporativos**: visualización y creación/edición de usuarios corporativos.
 * - **SSO — Empresas**: visualización de empresas (solo rol Corporativo).
 * - **SSO — Usuarios**: visualización, creación y edición de usuarios regulares.
 * - **SSO — Roles**: visualización de roles y relación factura manual (solo rol Empresa).
 * - **Gestión — Tipo Documento**: CRUD completo de tipos de documento.
 * - **Gestión — Tipo Proveedor**: CRUD completo de tipos de proveedor.
 * - **Gestión — Relación Proveedor-Usuario**: CRUD completo de relaciones proveedor-usuario.
 *
 * @param {boolean} isSuper - Indica si el usuario tiene el rol "Super".
 * @param {boolean} isCorporativo - Indica si el usuario tiene el rol "Corporativo".
 * @param {boolean} isEmpresa - Indica si el usuario tiene el rol "Empresa".
 * @param {boolean} isUser - Indica si el usuario tiene el rol "Operativo".
 * @param {(roleCheck: boolean, sectionKey: string) => boolean} canViewByRoleOrSectionList
 *   Helper que verifica si el rol cumple la condición Y la sección está en la lista del endpoint.
 * @param {(roleCheck: boolean, actionKey: string) => boolean} canPerformByRoleOrActionList
 *   Helper que verifica si el rol cumple la condición Y la acción está en la lista del endpoint.
 * @returns Objeto plano con booleanos para cada permiso del módulo Host.
 */
export const getHostPermissions = (
  isSuper: boolean,
  isCorporativo: boolean,
  isEmpresa: boolean,
  isUser: boolean,
  canViewByRoleOrSectionList: (roleCheck: boolean, sectionKey: string) => boolean,
  canPerformByRoleOrActionList: (roleCheck: boolean, actionKey: string) => boolean
) => {
  return {
    // Permisos generales de gestión
    canViewManagementSection: isSuper || isCorporativo || isEmpresa,

    // Permisos SSO - Corporates
    canViewCorporates: isSuper,
    canViewCorporateButtons: isSuper,
    canCreateCorporateUsers: isSuper,
    canEditCorporateUsers: isSuper || isCorporativo,

    // Permisos SSO - Companies
    canViewCompanies: isCorporativo,

    // Permisos SSO - Users
    canViewUsers: isSuper || isCorporativo || isEmpresa,
    canViewRegularUserButtons: isCorporativo || isEmpresa,
    canCreateRegularUsers: isCorporativo || isEmpresa,
    canEditRegularUsers: isCorporativo || isEmpresa,

    // Permisos SSO - Roles
    canViewRoles: isEmpresa,
    canViewRelacionFacturaManual: isEmpresa,

    // Permisos de gestión - Tipo Documento
    canViewDocumentType: canViewByRoleOrSectionList(isUser, 'GestionarTipoDocumento'),
    canViewListDocumentType: canPerformByRoleOrActionList(isUser, 'VerTipoDocumento'),
    canCreateDocumentType: canPerformByRoleOrActionList(isUser, 'CrearTipoDocumento'),
    canEditDocumentType: canPerformByRoleOrActionList(isUser, 'EditarTipoDocumento'),
    canDeleteDocumentType: canPerformByRoleOrActionList(isUser, 'EliminarTipoDocumento'),

    // Permisos de gestión - Tipo Proveedor
    canViewProviderType: canViewByRoleOrSectionList(isUser, 'TipoProveedor'),
    canViewListProviderType: canPerformByRoleOrActionList(isUser, 'VerTipoProveedor'),
    canCreateProviderType: canPerformByRoleOrActionList(isUser, 'CrearTipoProveedor'),
    canEditProviderType: canPerformByRoleOrActionList(isUser, 'EditarTipoProveedor'),
    canDeleteProviderType: canPerformByRoleOrActionList(isUser, 'EliminarTipoProveedor'),

    // Permisos de gestión - Relación Proveedor Usuario
    canViewProviderUserRelation: canViewByRoleOrSectionList(isUser, 'RelacionUsuarioProveedor'),
    canViewListProviderUserRelation: canPerformByRoleOrActionList(isUser, 'VerRelacionProveedorUsuario'),
    canCreateProviderUserRelation: canPerformByRoleOrActionList(isUser, 'CrearRelacionProveedorUsuario'),
    canEditProviderUserRelation: canPerformByRoleOrActionList(isUser, 'EditarRelacionProveedorUsuario'),
    canDeleteProviderUserRelation: canPerformByRoleOrActionList(isUser, 'EliminarRelacionProveedorUsuario'),
  };
};

